import {
  Typography,
  Box,
  Paper,
  Button,
  IconButton,
  Alert,
  Snackbar,
  Chip,
  Card,
  CardContent,
  Grid,
  Divider,
  Avatar,
  TextField,
  InputAdornment,
} from '@mui/material';
import { formatDate, getAge } from '../utils/formatters';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Cake as CakeIcon,
  LocationOn as LocationIcon,
  Search as SearchIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import { useState } from 'react';
import personsService from '../services/personsService';
import { usePersons } from '../hooks/queries';
import useDialog from '../hooks/useDialog';
import useSnackbar from '../hooks/useSnackbar';
import LoadingState from '../components/ui/LoadingState';
import EmptyState from '../components/ui/EmptyState';
import ErrorState from '../components/ui/ErrorState';
import CascadeDeleteDialog from '../components/ui/CascadeDeleteDialog';
import PersonForm from '../components/forms/PersonForm';
import AppointmentFormDialog from '../components/AppointmentFormDialog';
import { useThemeCustom } from '../hooks/useThemeCustom';
import { useContextualHint } from '../hooks/useContextualHint';
import ContextualHint from '../components/ContextualHint';
import { filterPaper, pageHeader } from '../styles/pageStyles';

export default function PersonsPage() {
  const { getLabel, getIcon } = useThemeCustom();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [selectedPersonForAppointment, setSelectedPersonForAppointment] = useState(null);

  // ✅ Custom Hooks - React Query
  const { items: persons, loading, error, reload, create, update, deleteItem } = usePersons();
  const formDialog = useDialog();
  const deleteDialog = useDialog();
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();

  const hint = useContextualHint('persons', {});

  // ✅ Handlers
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }

    try {
      setSearching(true);
      const response = await personsService.getAll({ search: searchQuery });
      setSearchResults(response);
    } catch (err) {
      showError('Erro ao buscar: ' + (err.response?.data?.message || err.message));
    } finally {
      setSearching(false);
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
  };

  const handleOpenAppointmentForm = (person) => {
    setSelectedPersonForAppointment(person);
  };

  const handleCloseAppointmentForm = () => {
    setSelectedPersonForAppointment(null);
  };

  const handleAppointmentCreated = () => {
    handleCloseAppointmentForm();
    showSuccess('Agendamento criado com sucesso!');
  };

  const handleCreate = async (data) => {
    try {
      await create(data);
      formDialog.handleClose();
      showSuccess('Pessoa criada com sucesso!');
    } catch (err) {
      showError('Erro ao criar: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleUpdate = async (data) => {
    try {
      await update(formDialog.data.id, data);
      formDialog.handleClose();
      showSuccess('Pessoa atualizada com sucesso!');
    } catch (err) {
      showError('Erro ao atualizar: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSubmit = formDialog.isEdit ? handleUpdate : handleCreate;

  // ✅ Loading/Error States
  if (loading) return <LoadingState message="Carregando pessoas..." />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  const displayPersons = searchResults !== null ? searchResults : (persons || []);

  return (
    <Box>
      <Box sx={pageHeader}>
        <Box>
          <Typography variant="h4" fontWeight="bold" color="primary.main">
            {getLabel('persons')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {getLabel('personsDescription')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => formDialog.handleOpen(null)}
        >
          Nova Pessoa
        </Button>
      </Box>

      <ContextualHint hint={hint} />

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          Gerencie clientes, pacientes, alunos e demais pessoas vinculadas aos seus estabelecimentos.
          Apenas nome e telefone são obrigatórios.
        </Typography>
      </Alert>

      {/* Campo de Busca */}
      <Paper sx={filterPaper}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            fullWidth
            placeholder="Buscar por nome, telefone, email ou CPF..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleSearchKeyPress}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={searching}
            sx={{ minWidth: 120 }}
          >
            {searching ? 'Buscando...' : 'Buscar'}
          </Button>
          {searchResults !== null && (
            <Button
              variant="outlined"
              onClick={handleClearSearch}
              sx={{ minWidth: 120 }}
            >
              Limpar
            </Button>
          )}
        </Box>
        {searchResults !== null && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {searchResults.length} resultado(s) encontrado(s)
          </Typography>
        )}
      </Paper>

      {/* Empty State */}
      {displayPersons.length === 0 ? (
        <EmptyState
          icon={PersonIcon}
          title={searchResults !== null ? "Nenhum resultado encontrado" : "Nenhuma pessoa cadastrada"}
          message={searchResults !== null ? "Tente buscar com outros termos" : "Clique em 'Nova Pessoa' para começar"}
          action={
            searchResults === null ? (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => formDialog.handleOpen(null)}>
                Nova Pessoa
              </Button>
            ) : null
          }
        />
      ) : (
        <Grid container spacing={3}>
          {displayPersons.map((person) => (
            <Grid item xs={12} md={6} lg={3} key={person.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderLeft: '3px solid', borderLeftColor: 'primary.main' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  {/* Cabeçalho do Card */}
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2 }}>
                    {/* Foto da Pessoa */}
                    <Avatar
                      src={person.photoUrl}
                      alt={person.name}
                      sx={{
                        width: 64,
                        height: 64,
                        bgcolor: 'grey.300',
                        color: 'grey.600',
                      }}
                    >
                      {!person.photoUrl && <PersonIcon sx={{ fontSize: 40 }} />}
                    </Avatar>
                    
                    {/* Informações Principais */}
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom noWrap>
                        {person.name}
                      </Typography>
                      {person.gender && (
                        <Chip 
                          label={person.gender} 
                          size="small" 
                          color="primary" 
                          sx={{ mb: 1 }} 
                        />
                      )}
                    </Box>
                    
                    {/* Botões de Ação */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => handleOpenAppointmentForm(person)}
                        title="Agendar consulta"
                      >
                        <CalendarIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => formDialog.handleOpen(person)}
                        title="Editar pessoa"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => deleteDialog.handleOpen(person)}
                        title="Excluir pessoa"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Informações de Contato */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <PhoneIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {person.phone}
                      </Typography>
                    </Box>
                    {person.email && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <EmailIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {person.email}
                        </Typography>
                      </Box>
                    )}
                    {person.birthDate && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CakeIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(person.birthDate)} ({getAge(person.birthDate)} anos)
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* CPF */}
                  {person.cpf && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      <strong>CPF:</strong> {person.cpf}
                    </Typography>
                  )}

                  {/* Endereço */}
                  {(person.addressStreet || person.addressCity) && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <LocationIcon fontSize="small" color="action" sx={{ mt: 0.5 }} />
                        <Box>
                          {person.addressStreet && (
                            <Typography variant="body2" color="text.secondary">
                              {person.addressStreet}
                              {person.addressNumber && `, ${person.addressNumber}`}
                              {person.addressComplement && ` - ${person.addressComplement}`}
                            </Typography>
                          )}
                          {person.addressNeighborhood && (
                            <Typography variant="body2" color="text.secondary">
                              {person.addressNeighborhood}
                            </Typography>
                          )}
                          {(person.addressCity || person.addressState) && (
                            <Typography variant="body2" color="text.secondary">
                              {person.addressCity}{person.addressState && ` - ${person.addressState}`}
                            </Typography>
                          )}
                          {person.addressZipCode && (
                            <Typography variant="body2" color="text.secondary">
                              CEP: {person.addressZipCode}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </>
                  )}

                  {/* Notas */}
                  {person.notes && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        {person.notes}
                      </Typography>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Person Form Dialog */}
      <PersonForm
        open={formDialog.open}
        onClose={formDialog.handleClose}
        onSubmit={handleSubmit}
        data={formDialog.data}
      />

      {/* Delete Confirmation Dialog */}
      <CascadeDeleteDialog
        open={deleteDialog.open}
        onClose={deleteDialog.handleClose}
        onDeleted={() => {
          deleteDialog.handleClose();
          showSuccess('Excluído com sucesso!');
          reload();
        }}
        deletePath={`/persons/${deleteDialog.data?.id}`}
        entityName={deleteDialog.data?.name}
      />

      {/* Appointment Form Dialog - auto-suficiente, carrega próprios dados */}
      {selectedPersonForAppointment && (
        <AppointmentFormDialog
          open={!!selectedPersonForAppointment}
          onClose={handleCloseAppointmentForm}
          onSuccess={handleAppointmentCreated}
          prefillData={{
            clientName: selectedPersonForAppointment.name,
            clientPhone: selectedPersonForAppointment.phone,
            clientEmail: selectedPersonForAppointment.email || '',
          }}
        />
      )}      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={hideSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
